import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .channel-program-list-print, .channel-program-list-print * {
            visibility: visible;
        }
        .channel-program-list-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            color: #000 !important;
            background: #fff !important;
        }
        .channel-program-list-print .event-row {
            cursor: default !important;
            border-bottom: 1px solid #ddd !important;
            padding: 4px 8px !important;
            margin-left: 0 !important;
            break-inside: avoid;
        }
        .channel-program-list-print .date-group {
            break-inside: avoid-page;
        }
        .channel-program-list-print .date-header {
            background: #f0f0f0 !important;
            padding: 4px 8px !important;
            margin-left: 0 !important;
            border-left: 3px solid #333 !important;
            break-after: avoid;
        }
        .channel-program-list-print .hour-divider {
            margin-left: 0 !important;
        }
        .channel-program-list-print .print-header {
            border-bottom: 2px solid #000;
            padding-bottom: 8px;
            margin-bottom: 12px;
        }
        .channel-program-list-print .badge {
            border: 1px solid #999 !important;
            background: #fff !important;
            color: #000 !important;
            font-weight: bold !important;
        }
        .channel-program-list-print .text-body-secondary,
        .channel-program-list-print .text-body-tertiary {
            color: #555 !important;
        }
    }
`;

@observer
class ChannelProgramList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            events: [],
            loading: false,
            periodDays: 7,
        };
    }

    componentDidMount() {
        this.loadEvents();
    }

    loadEvents = async () => {
        const { channelId } = this.props;
        const { periodDays } = this.state;
        if (!channelId) return;

        this.setState({ loading: true });

        const now = new Date();
        const startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + periodDays);

        const response = await util.fetchApi('/api/broadcastevents/', { method: 'GET' }, {
            channel: channelId,
            scheduledStartFrom: startDate.toISOString(),
            scheduledEndTo: endDate.toISOString(),
            overlapMode: 'true',
            limit: 1000,
        });

        if (response && response.status === 200) {
            const sorted = (response.data || []).sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart));
            this.setState({ events: sorted, loading: false });
        } else {
            this.setState({ events: [], loading: false });
        }
    }

    handlePeriodChange = (e) => {
        const periodDays = parseInt(e.target.value, 10);
        this.setState({ periodDays }, () => {
            this.loadEvents();
        });
    }

    handleEventClick = (event) => {
        const { stores, drawerLevel = 1 } = this.props;
        const { appState, broadcastEventStore, userStore } = stores;
        const { isAdmin, isCustomerAdmin } = userStore;
        const canEdit = isAdmin || isCustomerAdmin;

        broadcastEventStore.updateKeyValue('broadcastEvent', event);

        if (canEdit) {
            appState.openDrawer('broadcastEventEdit', {
                height: appState.drawerHeightMediumLarge,
                id: event.id,
                callback: () => this.loadEvents(),
            }, drawerLevel + 1);
        } else {
            appState.openDrawer('broadcastEventView', {
                height: appState.drawerHeightMediumLarge,
                id: event.id,
                callback: () => this.loadEvents(),
            }, drawerLevel + 1);
        }
    }

    handleEditChannel = () => {
        const { channelId, stores, drawerLevel = 1 } = this.props;
        const { appState, broadcastChannelStore } = stores;
        const channel = broadcastChannelStore.broadcastChannels.find(c => c.id === channelId);
        if (!channel) return;
        broadcastChannelStore.updateKeyValue('broadcastChannel', channel);
        appState.openDrawer('broadcastChannelEdit', {
            height: appState.drawerHeightMediumLarge,
            id: channel.id,
            callback: () => this.loadEvents(),
        }, drawerLevel + 1);
    }

    handlePrint = () => {
        window.print();
    }

    closeDrawer = () => {
        const { callback = () => {}, stores, drawerLevel = 1 } = this.props;
        const { appState } = stores;
        appState.toggleDrawer(false, drawerLevel);
        callback();
    }

    formatDuration(startDate, endDate) {
        const durationMin = Math.round((endDate - startDate) / 60000);
        const hours = Math.floor(durationMin / 60);
        const mins = durationMin % 60;
        if (hours > 0 && mins > 0) return `${hours}t ${mins}m`;
        if (hours > 0) return `${hours}t`;
        return `${mins}m`;
    }

    formatTime(date) {
        return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    }

    formatDateHeader(date) {
        const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
        const str = date.toLocaleDateString('nb-NO', options);
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    getEventTypeBadge(eventType) {
        const types = {
            program: { label: 'Program', cls: 'bg-primary' },
            live: { label: 'Live', cls: 'bg-danger' },
            replay: { label: 'Reprise', cls: 'bg-secondary' },
            commercial: { label: 'Reklame', cls: 'bg-warning text-dark' },
            news: { label: 'Nyheter', cls: 'bg-info' },
            filler: { label: 'Fyllstoff', cls: 'bg-secondary' },
        };
        return types[eventType] || { label: eventType || '', cls: 'bg-secondary' };
    }

    groupEventsByDate(events) {
        const groups = {};
        events.forEach(event => {
            const start = new Date(event.scheduledStart);
            const dateKey = start.toISOString().split('T')[0];
            if (!groups[dateKey]) {
                groups[dateKey] = { date: new Date(dateKey + 'T00:00:00'), events: [] };
            }
            groups[dateKey].events.push(event);
        });
        return Object.values(groups).sort((a, b) => a.date - b.date);
    }

    render() {
        const { channelId, stores } = this.props;
        const { events, loading, periodDays } = this.state;
        const { broadcastChannelStore, userStore } = stores;
        const { user, isAdmin, isCustomerAdmin } = userStore;
        const canEdit = isAdmin || isCustomerAdmin;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const channel = broadcastChannelStore.broadcastChannels.find(c => c.id === channelId) || {};
        const now = new Date();
        const dateGroups = this.groupEventsByDate(events);

        const periodLabel = periodDays === 7 ? '7 dager' : periodDays === 14 ? '14 dager' : '30 dager';
        const printDateRange = (() => {
            const from = new Date();
            from.setHours(0, 0, 0, 0);
            const to = new Date(from);
            to.setDate(to.getDate() + periodDays);
            return `${from.toLocaleDateString('nb-NO')} - ${to.toLocaleDateString('nb-NO')}`;
        })();

        return (
            <div class='w-100 d-flex justify-content-center'>
                <style>{printStyles}</style>
                <div class='channel-program-list-print w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    {/* Print-only header */}
                    <div class='d-none d-print-block print-header px-3 mb-3'>
                        <h4 class='mb-1'>{channel.name || 'Kanal'} - Programoversikt</h4>
                        <div style='font-size: 0.85rem; color: #555;'>{periodLabel} ({printDateRange})</div>
                        <div style='font-size: 0.75rem; color: #888;'>
                            Skrevet ut: {now.toLocaleDateString('nb-NO')} {now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>

                    {/* Screen header */}
                    <div class='d-flex align-items-center justify-content-between px-3 mb-3 d-print-none'>
                        <div class='d-flex align-items-center'>
                            {channel.color && (
                                <div style={`width: 4px; height: 32px; background-color: ${channel.color}; border-radius: 2px; margin-right: 12px;`} />
                            )}
                            <div>
                                <h5 class='mb-0'>
                                    <i class='fa-solid fa-list me-2' />
                                    {channel.name || 'Kanal'}
                                </h5>
                                <small class='text-body-secondary'>Programoversikt</small>
                            </div>
                        </div>
                        <div class='d-flex align-items-center gap-2'>
                            <select
                                class='form-select form-select-sm'
                                style='width: auto;'
                                value={periodDays}
                                onChange={this.handlePeriodChange}
                            >
                                <option value={7}>7 dager</option>
                                <option value={14}>14 dager</option>
                                <option value={30}>30 dager</option>
                            </select>
                            {canEdit && (
                                <button
                                    type='button'
                                    class='btn btn-outline-secondary btn-sm'
                                    onClick={this.handleEditChannel}
                                    title='Rediger kanal'
                                >
                                    <i class='fa-solid fa-pen' />
                                </button>
                            )}
                            <button
                                type='button'
                                class='btn btn-outline-secondary btn-sm'
                                onClick={this.handlePrint}
                                title='Skriv ut programoversikt'
                            >
                                <i class='fa-solid fa-print' />
                            </button>
                        </div>
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div class='text-center p-4'>
                            <div class='spinner-border spinner-border-sm text-primary' role='status'>
                                <span class='visually-hidden'>Laster...</span>
                            </div>
                            <span class='ms-2 text-body-secondary'>Laster programoversikt...</span>
                        </div>
                    )}

                    {/* No events */}
                    {!loading && events.length === 0 && (
                        <div class='text-center p-4'>
                            <i class='fa-solid fa-calendar-xmark fa-2x text-body-secondary mb-2' />
                            <p class='text-body-secondary mb-0'>Ingen hendelser de neste {periodDays} dagene.</p>
                        </div>
                    )}

                    {/* Event list grouped by date */}
                    {!loading && dateGroups.map(group => {
                        let lastHour = -1;

                        return (
                            <div key={group.date.toISOString()} class='date-group mb-4'>
                                {/* Date header */}
                                <div class='date-header px-3 py-2 border-bottom' style={channel.color ? `border-left: 3px solid ${channel.color}; margin-left: 12px;` : 'margin-left: 12px;'}>
                                    <strong>{this.formatDateHeader(group.date)}</strong>
                                    <span class='text-body-secondary ms-2'>
                                        <small>{group.events.length} hendelse{group.events.length !== 1 ? 'r' : ''}</small>
                                    </span>
                                </div>

                                {/* Events for this date */}
                                {group.events.map(event => {
                                    const start = new Date(event.scheduledStart);
                                    const end = new Date(event.scheduledEnd);
                                    const isOnNow = now >= start && now < end;
                                    const eventHour = start.getHours();
                                    const showHourDivider = eventHour !== lastHour;
                                    lastHour = eventHour;

                                    const badge = this.getEventTypeBadge(event.eventType);
                                    const eventColor = event.backgroundColor || channel.color;

                                    return (
                                        <div key={event.id}>
                                            {/* Hour divider */}
                                            {showHourDivider && (
                                                <div class='hour-divider d-flex align-items-center px-3 mt-2' style='margin-left: 12px;'>
                                                    <small class='text-body-tertiary fw-bold' style='min-width: 50px;'>
                                                        {String(eventHour).padStart(2, '0')}:00
                                                    </small>
                                                    <div class='flex-grow-1 border-bottom' style='opacity: 0.3;' />
                                                </div>
                                            )}

                                            {/* Event row */}
                                            <div
                                                class={`event-row d-flex align-items-center px-3 py-2 border-bottom ${isOnNow ? 'bg-success bg-opacity-10' : ''}`}
                                                style='cursor: pointer; margin-left: 12px; transition: background-color 0.15s;'
                                                onClick={() => this.handleEventClick(event)}
                                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = isOnNow ? '' : (darkmode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'); }}
                                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                                            >
                                                {/* Color bar */}
                                                {eventColor && (
                                                    <div style={`width: 3px; height: 36px; background-color: ${eventColor}; border-radius: 2px; margin-right: 10px; flex-shrink: 0;`} />
                                                )}
                                                {!eventColor && <div style='width: 13px; flex-shrink: 0;' />}

                                                {/* Time */}
                                                <div style='min-width: 90px; flex-shrink: 0;'>
                                                    <span class='fw-bold' style='font-size: 0.9rem;'>{this.formatTime(start)}</span>
                                                    <span class='text-body-secondary mx-1'>-</span>
                                                    <span class='text-body-secondary' style='font-size: 0.85rem;'>{this.formatTime(end)}</span>
                                                </div>

                                                {/* Title and subtitle */}
                                                <div class='flex-grow-1 ms-2 text-truncate'>
                                                    <div class='d-flex align-items-center'>
                                                        {isOnNow && (
                                                            <span class='badge bg-danger me-2' style='font-size: 0.65rem;'>DIREKTE</span>
                                                        )}
                                                        <span class='fw-bold text-truncate' style='font-size: 0.9rem;'>
                                                            {event.title || 'Uten tittel'}
                                                        </span>
                                                    </div>
                                                    {event.subtitle && (
                                                        <small class='text-body-secondary text-truncate d-block'>{event.subtitle}</small>
                                                    )}
                                                </div>

                                                {/* Duration */}
                                                <div class='text-body-secondary text-end ms-2' style='min-width: 50px; flex-shrink: 0; font-size: 0.8rem;'>
                                                    {this.formatDuration(start, end)}
                                                </div>

                                                {/* Type badge */}
                                                {event.eventType && (
                                                    <div class='ms-2' style='flex-shrink: 0;'>
                                                        <span class={`badge ${badge.cls}`} style='font-size: 0.65rem;'>
                                                            {badge.label}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Arrow icon */}
                                                <div class='ms-2 text-body-tertiary d-print-none' style='flex-shrink: 0;'>
                                                    <i class='fa-solid fa-chevron-right' style='font-size: 0.7rem;' />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* Fixed bottom close button */}
                    <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-body fixed-bottom d-print-none' style='bottom: 0; left: 0;'>
                        <button type='button' class='btn btn-secondary rounded-pill' onClick={this.closeDrawer}>
                            <i class='fa-solid fa-xmark me-2' />
                            Lukk
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ChannelProgramList;
